import { useMutation } from '@tanstack/react-query'

export interface DraftInvoiceLineItem {
  description: string
  quantity: number
  unitPriceIdr: number
}

export interface DraftInvoicePayload {
  customerId: string
  boatId: string | null
  dueDate: string
  taxRate: number
  notes: string | null
  recipientName: string | null
  recipientEmail: string | null
  lineItems: DraftInvoiceLineItem[]
}

export interface DraftInvoiceResponse {
  draft: DraftInvoicePayload
  context: {
    contractCode: string
    customerName: string
    periodLabel: string
    monthlyShare: number
    taxRate: number
  }
}

async function postJson<TBody, TResponse>(path: string, body: TBody): Promise<TResponse> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

/**
 * Ask the AI proxy to draft a periodic invoice for a long-term contract.
 * The endpoint returns the line items + suggested due date but does NOT
 * create the invoice — the UI shows a preview, lets the user edit, then
 * POSTs to /api/invoices on confirm.
 */
export function useDraftInvoice() {
  return useMutation({
    mutationFn: (input: { contractId: string; period?: 'this-month' | 'next-month' }) =>
      postJson<typeof input, DraftInvoiceResponse>('/api/ai/draft-invoice', input),
  })
}
